import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import UserRole from '../../shared/userRole';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AlertDialog from '../../shared/alertDialog';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { PEOPLE_DEFAULT_ORDERING } from '../../../constants/people';
import {
  SYSTEM_ROLES,
  SYSTEM_ROLES_BY_ID,
  PRIMARY_ROLE_FIELD,
  ADDITIONAL_ROLE_FIELD,
} from '../../../constants/roles';
import { USER_STATUSES } from '../../../constants/statuses';
import {
  API_USERS_BASIC,
  api_rbac_users_scope_groups,
  api_rbac_users_scope_group,
} from '../../../constants/apiRoutes';
import { sticky } from '../../../constants/helperCssRules';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { personSelector } from '../../../store/selectors/personSelector';
import { personRolesSelector } from '../../../store/selectors/personRolesSelector';
import { rolesSelector } from '../../../store/selectors/rolesSelector';
import { getRoles, clearRoles } from '../../../store/modules/roles';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import {
  getUserRoles,
  clearUserRoles,
  updateUserRoles,
} from '../../../store/modules/personRoles';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import http from '../../../utility/http';
import {
  parseDuplicateParameters,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import { preparePeopleFilters } from '../../../utility/people';
import { useTranslations } from '../../../utility/useTranslations';
import {
  hasScopedAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  prepareRoles,
  prepareUsersScopeGroups,
  prepareSelectedFilters,
  prepareSystemRoles,
  prepareAdditionalRoleOptions,
  prepareUsersScopeInitialData,
  prepareUsersScopeFields,
  preparePrimaryRoleFormInitialData,
} from '../../../utility/roles';
import {
  isUserDeactivated,
  getItemById,
  isArrayEmpty,
} from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  description: {
    paddingBottom: spacing(4),
    ...sticky(primary.white, 170),
  },
  content: {
    display: 'flex',
    gridGap: spacing(8),
    width: 900,
  },
  scopeFormContent: {
    height: '100%',
  },
}));

const PeopleRolesPage = ({
  auth,
  accessControl,
  dispatch,
  navigate,
  ...rest
}) => {
  const { id } = useParams();

  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE_ROLES);

  const user = useSelector(personSelector);
  const roles = useSelector(rolesSelector);
  const [primaryRole, additionalRole] = useSelector(personRolesSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [directReports, setDirectReports] = useState([]);
  const [isPrimaryRoleDrawerOpened, setIsPrimaryRoleDrawerOpened] =
    useState(false);
  const [isAdditionalRoleDrawerOpened, setIsAdditionalRoleDrawerOpened] =
    useState(false);
  const [
    isDeleteAdditionalRoleDialogOpened,
    setIsDeleteAdditionalRoleDialogOpened,
  ] = useState(false);
  const [isUsersScopeDrawerOpened, setIsUsersScopeDrawerOpened] =
    useState(false);
  const [usersScopeGroups, setUsersScopeGroups] = useState([]);
  const [usersScopeGroup, setUsersScopeGroup] = useState(null);
  const [isDeleteUsersScopeDialogOpened, setIsDeleteUsersScopeDialogOpened] =
    useState(false);

  const userId = (!Number.isNaN(Number(id)) && +id) || -1;
  const canEdit = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_ROLES_MANAGE,
    accessControl,
    auth.id,
    userId
  );
  const isCurrentUser = auth.id === userId;
  const isDeactivatedUser = isUserDeactivated(user);
  const allRoles = useMemo(
    () => prepareRoles(translations.systemRoles, roles),
    [translations.systemRoles, roles]
  );
  const userPrimaryRole = getItemById(allRoles, primaryRole?.id);
  const userAdditionalRole = getItemById(allRoles, additionalRole?.id);
  const isAdditionalRoleDisabled = useMemo(
    () => primaryRole?.name === SYSTEM_ROLES.ADMIN,
    [primaryRole]
  );
  const isPrimaryRoleManagable =
    !isCurrentUser && !isDeactivatedUser && canEdit;
  const isAdditionalRoleManagable = !(
    isCurrentUser ||
    isAdditionalRoleDisabled ||
    isDeactivatedUser ||
    !canEdit
  );
  const isAdditionalRoleScopeButtonDisabled =
    isDeactivatedUser || isAdditionalRoleDisabled || !userAdditionalRole;
  const userAdditionalRoleScopeGroups = prepareUsersScopeGroups(
    usersScopeGroups,
    allUsers
  );
  const isAdmin = userPrimaryRole?.name === SYSTEM_ROLES.ADMIN;

  const userDirectReports = useMemo(
    () =>
      !isArrayEmpty(directReports)
        ? [{ name: translations.directReports, members: directReports }]
        : [],
    [translations, directReports]
  );
  const primaryRoleInitialData = preparePrimaryRoleFormInitialData(
    userPrimaryRole?.id,
    userAdditionalRole?.id
  );
  const primaryRoleOptions = useMemo(
    () => prepareSystemRoles(allRoles),
    [allRoles]
  );
  const filterRoleOptions = useMemo(
    () => prepareRoles(translations.systemRoles, roles, false, true),
    [translations.systemRoles, roles]
  );
  const additionalRoleInitialData = {
    additionalRoleId: userAdditionalRole?.id || null,
  };
  const additionalRoleOptions = useMemo(
    () => prepareAdditionalRoleOptions(allRoles),
    [allRoles]
  );

  const canAccessUserProfileCheck = useCallback(
    currentUserId =>
      isUserProfileAccessibleCheck(accessControl, auth.id, currentUserId),
    [auth, accessControl]
  );

  const onGoToUserProfile = useCallback(
    currentUserId => navigate(`/people/${currentUserId}`),
    [navigate]
  );

  const getAllUsers = useCallback(
    async (selectedFilters = {}) => {
      const { data } = await http.get(API_USERS_BASIC, {
        params: {
          ...PEOPLE_DEFAULT_ORDERING,
          [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
          [PARAMS.EXCLUDE]: [userId],
          ...selectedFilters,
        },
        paramsSerializer: d => parseDuplicateParameters(d),
      });

      return data;
    },
    [userId]
  );

  const getDirectReports = useCallback(async () => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [PARAMS.REPORT_TO]: userId,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });

    return setDirectReports(data);
  }, [userId]);

  const getUsersScopeGroups = useCallback(async () => {
    const { data } = await http.get(api_rbac_users_scope_groups(userId));

    return setUsersScopeGroups(data);
  }, [userId]);

  const peopleFilters = useMemo(
    () =>
      preparePeopleFilters(
        translations,
        jobTitles,
        tracks,
        divisions,
        departments,
        filterRoleOptions,
        allUsers,
        [],
        false,
        true
      ),
    [
      translations,
      jobTitles,
      tracks,
      divisions,
      departments,
      filterRoleOptions,
      allUsers,
    ]
  );
  const usersScopeInitialData = useMemo(
    () => prepareUsersScopeInitialData(usersScopeGroup, allUsers),
    [usersScopeGroup, allUsers]
  );
  const usersScopeFields = useMemo(
    () => prepareUsersScopeFields(getAllUsers, peopleFilters),
    [peopleFilters, getAllUsers]
  );

  const handleOpenPrimaryRoleDrawer = useCallback(() => {
    if (canEdit && !isCurrentUser && !isDeactivatedUser)
      setIsPrimaryRoleDrawerOpened(true);
  }, [canEdit, isCurrentUser, isDeactivatedUser]);

  const onClosePrimaryRoleDrawer = useCallback(() => {
    setIsPrimaryRoleDrawerOpened(false);
  }, []);

  const onSavePrimaryRole = async ({ primaryRoleId }) => {
    const updatedRoles = {
      role_ids: [
        primaryRoleId,
        ...(additionalRole && primaryRoleId >= SYSTEM_ROLES_BY_ID.MODERATOR_ID
          ? [additionalRole.id]
          : []),
      ],
    };

    showSuccessMessage(translations.managePrimaryRole.successMessages.edit);

    if (
      primaryRoleId === SYSTEM_ROLES_BY_ID.ADMIN_ID &&
      !isArrayEmpty(usersScopeGroups)
    ) {
      await http.delete(api_rbac_users_scope_groups(userId));
      setUsersScopeGroups([]);
    }

    return updateUserRoles(dispatch, userId, updatedRoles);
  };

  const handleOpenAdditionalRoleDrawer = useCallback(() => {
    if (
      !(
        isCurrentUser ||
        isAdditionalRoleDisabled ||
        isDeactivatedUser ||
        !canEdit
      )
    )
      setIsAdditionalRoleDrawerOpened(true);
  }, [isCurrentUser, isAdditionalRoleDisabled, isDeactivatedUser, canEdit]);

  const onCloseAdditionalRoleDrawer = useCallback(() => {
    setIsAdditionalRoleDrawerOpened(false);
  }, []);

  const onSaveAdditionalRole = async ({ additionalRoleId }) => {
    const updatedRoles = {
      role_ids: [primaryRole.id, additionalRoleId],
    };

    if (userAdditionalRole) {
      showSuccessMessage(
        translations.manageAdditionalRole.successMessages.edit
      );
    } else {
      showSuccessMessage(translations.manageAdditionalRole.successMessages.add);
    }

    return updateUserRoles(dispatch, userId, updatedRoles);
  };

  const handleOpenDeleteAdditionalRoleDialog = () =>
    setIsDeleteAdditionalRoleDialogOpened(true);

  const handleCloseDeleteAdditionalRoleDialog = () =>
    setIsDeleteAdditionalRoleDialogOpened(false);

  const onDeleteAdditionalRole = async () => {
    if (!isArrayEmpty(usersScopeGroups)) {
      await http.delete(api_rbac_users_scope_groups(userId));
      setUsersScopeGroups([]);
    }

    await updateUserRoles(dispatch, userId, {
      role_ids: [primaryRole.id],
    });
    showSuccessMessage(translations.deleteAdditionalRoleDialog.deleteSuccess);
    handleCloseDeleteAdditionalRoleDialog();
    onCloseAdditionalRoleDrawer();
  };

  const handleOpenAddUserScopesDrawer = useCallback(() => {
    if (!((canEdit && isCurrentUser) || isAdditionalRoleDisabled))
      setIsUsersScopeDrawerOpened(true);
  }, [canEdit, isCurrentUser, isAdditionalRoleDisabled]);

  const onCloseUsersScopeDrawer = useCallback(() => {
    setIsUsersScopeDrawerOpened(false);
    setUsersScopeGroup(null);
  }, []);

  const onSaveUsersScope = async values => {
    const scope = {
      name: values.name,
      criteria: prepareSelectedFilters(values.employees.selected),
    };

    if (values.id) {
      await http.patch(api_rbac_users_scope_group(userId, values.id), scope);
      showSuccessMessage(translations.manageUsersScope.successMessages.edit);
    } else {
      await http.post(api_rbac_users_scope_groups(userId), scope);
      showSuccessMessage(translations.manageUsersScope.successMessages.add);
    }

    return getUsersScopeGroups();
  };

  const handleOpenEditScopeGroup = group => {
    setUsersScopeGroup(group);
    setIsUsersScopeDrawerOpened(true);
  };

  const handleOpenDeleteScopeGroupDialog = () => {
    setIsDeleteUsersScopeDialogOpened(true);
  };

  const handleOpenDeleteScopeGroup = group => {
    setUsersScopeGroup(group);
    setIsDeleteUsersScopeDialogOpened(true);
  };

  const handleCloseDeleteScopeGroup = () => {
    setIsDeleteUsersScopeDialogOpened(false);
  };

  const onDeleteUsersScope = async () => {
    await http.delete(api_rbac_users_scope_group(userId, usersScopeGroup.id));
    setIsUsersScopeDrawerOpened(false);
    setIsDeleteUsersScopeDialogOpened(false);
    setUsersScopeGroup(null);
    showSuccessMessage(translations.deleteScopeDialog.deleteSuccess);

    return getUsersScopeGroups();
  };

  const getInitialData = useCallback(async () => {
    try {
      const [availableUsers, currentUserRoles] = await Promise.all([
        getAllUsers(),
        getUserRoles(dispatch, userId),
        getDirectReports(),
        getRoles(dispatch),
        getJobTitles(dispatch),
        getLevelsAndTracks(dispatch),
        getDivisionsAndDepartments(dispatch),
      ]);
      const [, hasAdditionalRole] = currentUserRoles;

      if (hasAdditionalRole) {
        await getUsersScopeGroups();
      }
      setAllUsers(availableUsers || []);
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch, userId, getAllUsers, getDirectReports, getUsersScopeGroups]);

  const cleanup = useCallback(() => {
    dispatch(clearRoles());
    dispatch(clearUserRoles());
    dispatch(clearJobTitles());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <PeoplePageContainer
      {...rest}
      translations={translations}
      userId={userId}
      auth={auth}
      accessControl={accessControl}
      dispatch={dispatch}
      navigate={navigate}
      shouldPassProps={false}
    >
      {!isInitialLoad && (
        <div>
          <Typography variant="body2" className={classes.description}>
            {translations.description}
          </Typography>
          <div className={classes.content}>
            <UserRole
              translations={translations.primaryRole}
              role={userPrimaryRole?.label}
              scopeGroups={userDirectReports}
              isManagable={isPrimaryRoleManagable}
              canAccessUserProfileCheck={canAccessUserProfileCheck}
              onGoToUserProfile={onGoToUserProfile}
              onManageRole={handleOpenPrimaryRoleDrawer}
            />
            {!isAdmin && (
              <UserRole
                translations={translations.additionalRole}
                role={userAdditionalRole?.label}
                scopeGroups={userAdditionalRoleScopeGroups}
                isManagable={isAdditionalRoleManagable}
                isScopeDisabled={isAdditionalRoleScopeButtonDisabled}
                hasAddScope={canEdit}
                canAccessUserProfileCheck={canAccessUserProfileCheck}
                onGoToUserProfile={onGoToUserProfile}
                onManageRole={handleOpenAdditionalRoleDrawer}
                onAddScope={handleOpenAddUserScopesDrawer}
                onEditScope={handleOpenEditScopeGroup}
                onDeleteScope={handleOpenDeleteScopeGroup}
              />
            )}
          </div>
          <CustomFormDrawer
            translations={translations.managePrimaryRole}
            titleText={translations.managePrimaryRole.title}
            initialData={primaryRoleInitialData}
            isOpened={isPrimaryRoleDrawerOpened}
            fields={PRIMARY_ROLE_FIELD}
            roles={primaryRoleOptions}
            onClose={onClosePrimaryRoleDrawer}
            onSave={onSavePrimaryRole}
            hasCancelButton
            isInitialValid
            hideDelete
          />
          <CustomFormDrawer
            translations={translations.manageAdditionalRole}
            titleText={translations.manageAdditionalRole.title}
            initialData={additionalRoleInitialData}
            hideDelete={!additionalRole}
            isOpened={isAdditionalRoleDrawerOpened}
            fields={ADDITIONAL_ROLE_FIELD}
            roles={additionalRoleOptions}
            onClose={onCloseAdditionalRoleDrawer}
            onSave={onSaveAdditionalRole}
            onDelete={handleOpenDeleteAdditionalRoleDialog}
            hasCancelButton
            isInitialValid
          />
          <CustomFormDrawer
            customContentClass={classes.scopeFormContent}
            translations={translations.manageUsersScope}
            initialData={usersScopeInitialData}
            isOpened={isUsersScopeDrawerOpened}
            isInitialValid={!!usersScopeGroup}
            hideDelete={!usersScopeGroup}
            fields={usersScopeFields}
            roles={additionalRoleOptions}
            onClose={onCloseUsersScopeDrawer}
            onSave={onSaveUsersScope}
            onDelete={handleOpenDeleteScopeGroupDialog}
            hasCancelButton
            isLarge
          />
          <AlertDialog
            translations={translations.deleteAdditionalRoleDialog}
            isOpened={isDeleteAdditionalRoleDialogOpened}
            onClose={handleCloseDeleteAdditionalRoleDialog}
            onConfirm={onDeleteAdditionalRole}
            isWarning
            isLarge
          />
          <AlertDialog
            translations={translations.deleteScopeDialog}
            isOpened={isDeleteUsersScopeDialogOpened}
            onClose={handleCloseDeleteScopeGroup}
            onConfirm={onDeleteUsersScope}
            isWarning
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

PeopleRolesPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(PeopleRolesPage);
